import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import MetaTags from "../../componentes/MetaTags";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faFileAlt,
  faDownload,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";
import { fetchReports } from "../../api/reports";
import "../../styles/cyber-bg.css";
import "../../styles/circuit-background.css";

const ReportCard = ({ report, index }) => {
  const formatFileSize = (sizeStr) => {
    if (typeof sizeStr === "string" && sizeStr.includes("MB")) {
      return sizeStr;
    }
    return sizeStr || "3.8MB";
  };

  const getFileType = () => {
    if (report.file_url) {
      const extension = report.file_url.split(".").pop()?.toLowerCase();
      switch (extension) {
        case "pdf":
          return { icon: faFilePdf, label: "PDF" };
        case "doc":
        case "docx":
          return { icon: faFileAlt, label: "Word Document" };
        case "xls":
        case "xlsx":
          return { icon: faFileAlt, label: "Excel Spreadsheet" };
        case "ppt":
        case "pptx":
          return { icon: faFileAlt, label: "Presentation" };
        default:
          return {
            icon: faFileAlt,
            label: extension ? extension.toUpperCase() : "Document",
          };
      }
    }
    return { icon: faFilePdf, label: "PDF" };
  };

  const fileType = getFileType();

  return (
  <motion.div
  key={report.id || index}
  initial={{ opacity: 0, y: 30 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{
    delay: index * 0.1,
    type: "spring",
    stiffness: 100,
  }}
  className="group bg-white/90 backdrop-blur-sm rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:-translate-y-2 flex flex-col"
>
  {/* IMAGE */}
  <div className="relative h-44 w-full bg-gray-50 overflow-hidden">
    {report.cover_image_url ? (
      <img
        src={report.cover_image_url}
        alt={report.title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    ) : (
      <div className="flex items-center justify-center h-full">
        <div className="p-6">
          <FontAwesomeIcon
            icon={faFileAlt}
            className="text-6xl text-primary"
          />
        </div>
      </div>
    )}

    {/* File type badge */}
    <div className="absolute top-3 right-3 bg-white/90 text-xs px-2 py-1 rounded-md shadow-sm font-medium text-gray-600">
      {fileType.label}
    </div>
  </div>

  {/* CONTENT */}
  <div className="p-5 flex flex-col flex-grow">

    {/* TITLE */}
    <h3 className="text-base font-semibold text-gray-800 mb-3 line-clamp-2 group-hover:text-primary transition">
      {report.title}
    </h3>

    {/* META */}
    <div className="flex flex-wrap gap-2 mb-4">
      <span className="flex items-center text-xs bg-gray-100 px-2.5 py-1 rounded-full text-gray-600">
        <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-primary" />
        {report.year}
      </span>

      {report.report_type && (
        <span className="flex items-center text-xs bg-gray-100 px-2.5 py-1 rounded-full text-gray-600">
          <FontAwesomeIcon icon={faFileAlt} className="mr-1 text-primary" />
          {report.report_type.name}
        </span>
      )}
    </div>

    {/* PUSH BUTTON DOWN */}
    <div className="mt-auto">
      <a
        href={report.file_url}
        download
        className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white text-sm font-medium py-2.5 rounded-lg hover:bg-primary-dark transition-all shadow-sm hover:shadow-md"
      >
        <FontAwesomeIcon icon={faDownload} />
        Download
        <span className="text-xs opacity-80">
          ({formatFileSize(report.file_size)})
        </span>
      </a>
    </div>
  </div>
</motion.div>
  );
};

const EmptyReports = ({ message }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="col-span-2 bg-white p-12 rounded-xl shadow-md text-center text-gray-500 border border-dashed border-gray-300"
  >
    <FontAwesomeIcon icon={faFileAlt} className="text-5xl mb-4 text-gray-400" />
    <h3 className="text-xl font-semibold mb-2">No reports available</h3>
    <p>{message}</p>
  </motion.div>
);

const Reports = () => {
  const { t, i18n } = useTranslation();
  const [activeYear, setActiveYear] = useState(null);

  const {
    data: reportsData,
    isLoading: reportsLoading,
  } = useQuery({
    queryKey: ["reports", i18n.language],
    queryFn: () => fetchReports({ lang: i18n.language }),
  });

  const years = useMemo(() => {
    if (!reportsData) return [];
    return [...new Set(reportsData.map((report) => report.year))].sort(
      (a, b) => b - a
    );
  }, [reportsData]);

  useEffect(() => {
    if (!reportsLoading && years.length > 0 && !activeYear) {
      setActiveYear(years[0]);
    }
  }, [years, reportsLoading]);

  const filteredReports = useMemo(() => {
    if (!reportsData || !activeYear) return [];
    return reportsData.filter((report) => report.year === activeYear);
  }, [reportsData, activeYear]);

  const renderContent = () => {
    if (reportsLoading) {
      return <FullscreenSpinner className="py-12" />;
    }

    // Check if no data exists instead of showing an error
    if (!reportsData || reportsData.length === 0) {
      return (
        <div className="bg-white p-10 rounded-xl shadow-md text-center text-gray-500">
          <FontAwesomeIcon
            icon={faFileAlt}
            className="text-5xl mb-4 text-gray-400"
          />
          <h3 className="text-xl font-semibold">{t("reports.noReports")}</h3>
          <p>{t("reports.noDataMessage", "No reports have been added yet.")}</p>
        </div>
      );
    }

    return (
      <>
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-h-5 font-bold mb-8 text-gray-800 border-b-2 border-primary pb-2 inline-block"
        >
          {`${activeYear} ${t("reports.title")}`}
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
          {filteredReports.length > 0 ? (
            filteredReports.map((report, index) => (
              <ReportCard key={report.id} report={report} index={index} />
            ))
          ) : (
            <EmptyReports
              message={t("reports.noReportsForYear", { year: activeYear })}
            />
          )}
        </div>
      </>
    );
  };
  return (
    <>
      <MetaTags title="Reports — Ethio-CERT" description="Research and publications from Ethio-CERT." url="https://ethiocert.gov.et/resources/reports" />
      <PageHeader />
      <GradientBanner
        title={t("reports.title")}
        description={t("reports.subtitle")}
        path={[
          { label: t("header.home", "Home"), link: "/" },
          { label: t("header.reports", "Reports"), link: null },
        ]}
        backgroundIcons={[faFilePdf, faFileAlt, faDownload]}
      />
      <div className="circuit-page-background">
        <div className="py-16 font-nunito-sans">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[220px] flex-shrink-0">
                <div className="bg-primary text-white p-4 rounded-t-lg shadow-md">
                  <h2 className="text-h-7 font-bold uppercase tracking-wide">
                    {t("reports.title")}
                  </h2>
                </div>
                <div className="bg-white shadow-lg rounded-b-lg overflow-hidden sticky top-24">
                  {years.map((year, idx) => (
                    <motion.button
                      key={year}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      onClick={() => setActiveYear(year)}
                      className={`flex items-center justify-between w-full p-4 text-left border-b border-gray-100 hover:bg-gray-50 transition-all ${activeYear === year
                          ? "font-bold text-primary border-l-4 border-l-primary pl-3"
                          : "border-l-4 border-l-transparent"
                        }`}
                    >
                      <span className="text-lg">{year}</span>
                      <span
                        className={`transform transition-transform ${activeYear === year ? "rotate-90" : ""
                          }`}
                      >
                        →
                      </span>
                    </motion.button>
                  ))}
                </div>
              </div>
              <div className="flex-1 min-w-0">{renderContent()}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Reports;